// Monetização — a camada que os fluxos chamam.
//
//   await depoisDaAcao('TREINO')     intersticial (se a regra do servidor
//                                    deixar) e, depois dele, a oferta do
//                                    Premium (1x/dia). Resolve quando tudo
//                                    fechou. NUNCA lança: anúncio não pode
//                                    travar um fluxo.
//   await pedirRewarded({tipo, eventKeyOrigem})
//                                    vídeo com recompensa; resolve com o que
//                                    o SERVIDOR concedeu (via SSV).
//   bonusDisponivel(moeda)           se a tela de VitaCoins deve oferecer o vídeo.
//   abrirFunilPremium({origem})      oferta → lembrete → comparativo → planos.
//
// É um módulo, e não um Context, de propósito: a tela de resultado do
// ScanFood (/result) vive fora da PlataformaPessoal, e o funil precisa abrir
// dos dois lados. O <MonetizacaoHost/> (montado no AppRoutes) se registra
// aqui para desenhar o funil; o AdsContext registra a campanha própria.

import { Capacitor } from '@capacitor/core';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import {
  carregarEstadoAnuncios,
  ESTADO_DESLIGADO,
  intersticial,
  prepararRewarded,
  registrarMetrica,
} from './anunciosApi';
import * as admob from './admobService';

const st = {
  uid: null,
  // Tier visto pelo app AGORA (useEntitlements). Comprou o Premium ⇒ false na
  // hora, sem esperar o webhook atualizar o servidor.
  podeAdsCliente: false,
  host: null, // { abrirFunil(opts): Promise<void> }
  campanhaPropria: null, // () => Promise<boolean>
  decisoes: new Map(), // gatilho → {promessa, em}
  ultimoEstado: null,
  ocupado: false,
};

/** O Host chama isto a cada mudança de login/tier. */
export function configurarMonetizacao({ uid, podeAds }) {
  if (uid !== st.uid) {
    st.uid = uid || null;
    st.decisoes.clear();
  }
  st.podeAdsCliente = !!podeAds;
}

export function registrarHost(host) {
  st.host = host;
  return () => {
    if (st.host === host) st.host = null;
  };
}

/** AdsContext registra: mostra uma campanha própria se houver e resolve ao fechar. */
export function registrarCampanhaPropria(fn) {
  st.campanhaPropria = fn;
  return () => {
    if (st.campanhaPropria === fn) st.campanhaPropria = null;
  };
}

function comTimeout(promessa, ms, valor = null) {
  return Promise.race([
    Promise.resolve(promessa).catch(() => valor),
    new Promise((resolve) => setTimeout(() => resolve(valor), ms)),
  ]);
}

export async function estadoAtual() {
  if (!st.uid) return ESTADO_DESLIGADO;
  const est = await comTimeout(carregarEstadoAnuncios(st.uid), 4000, ESTADO_DESLIGADO);
  st.ultimoEstado = est;
  return est;
}

/**
 * O intersticial do AdMob está no comando do slot pós-ação? Síncrono, pelo
 * último estado lido. Enquanto estiver, o motor antigo de campanhas próprias
 * (registrarAcaoAd, que dispara sozinho a cada N ações) fica quieto — a
 * campanha própria passa a entrar pelo mesmo slot, com prioridade, via
 * depoisDaAcao. Sem isto seriam dois anúncios na mesma ação.
 */
export function admobIntersticialLigado() {
  const cfg = st.ultimoEstado?.cfg;
  return !!(admob.nativo() && cfg?.ADMOB_ATIVO && cfg?.INTERSTICIAL_ATIVO);
}

function anunciosPossiveis(est) {
  // UID de teste (CONFIG/ANUNCIOS.UIDS_TESTE): o servidor manda modoTeste e a
  // conta é tratada como grátis para os anúncios, mesmo sendo Premium ou
  // vinculada a academia — senão a conta de QA nunca veria nada.
  const tierLocal = st.podeAdsCliente || est?.modoTeste === true;
  return admob.nativo() && tierLocal && est?.podeAds === true;
}

/** A conta atual está na lista de teste (último estado lido)? */
export function modoTesteAtivo() {
  return st.ultimoEstado?.modoTeste === true;
}

/**
 * Começa a perguntar ao servidor ANTES de chegar a hora (enquanto a
 * celebração anima), para o intersticial não esperar a ida e volta.
 * @param {string} gatilho TREINO | REFEICAO | LICAO | MAPA | ANALISE_CORPORAL
 */
export function anteciparDecisao(gatilho) {
  if (!st.uid || !admob.nativo()) return;
  if (!st.podeAdsCliente && !modoTesteAtivo()) return;
  const atual = st.decisoes.get(gatilho);
  if (atual && Date.now() - atual.em < 60000) return;
  const promessa = (async () => {
    const est = await estadoAtual();
    if (!anunciosPossiveis(est) || !est.cfg.ADMOB_ATIVO || !est.cfg.INTERSTICIAL_ATIVO) {
      return { mostrar: false };
    }
    // O consentimento (UMP/ATT) NÃO é pedido aqui: só na hora de mostrar.
    const d = await intersticial(gatilho, 'DECIDIR');
    // E O ANÚNCIO TAMBÉM VAI CARREGANDO. Antecipar só a decisão não resolvia
    // o problema que esta função existe para resolver: a ida e volta ao
    // servidor saía da frente, mas a carga do anúncio continuava acontecendo
    // dentro de `depoisDaAcao`, a frio, com o fluxo parado esperando. Uma
    // carga fria passa dos segundos, o slot desistia, e o intersticial ficava
    // pronto logo DEPOIS de a tela já ter seguido — nunca aparecia.
    // Só aquece quando a resposta foi sim: nada de pedir inventário à toa.
    // `aquecer` é silencioso por construção (ver prepararSilencioso): o UMP e
    // o ATT continuam saindo só no momento de exibir, nunca por cima da
    // animação de celebração.
    if (d?.mostrar) admob.aquecer(est.cfg).catch(() => {});
    return d;
  })().catch(() => ({ mostrar: false }));
  st.decisoes.set(gatilho, { promessa, em: Date.now() });
}

/**
 * Intersticial depois de uma ação + oferta do Premium. Resolve quando tudo
 * fechou; nunca lança.
 * @param {string} gatilho
 * @return {Promise<{exibido:boolean, oferta:boolean}>}
 */
export async function depoisDaAcao(gatilho, { aoTelaPropria } = {}) {
  const nada = { exibido: false, oferta: false };
  // A campanha própria e o funil do Premium são telas do PRÓPRIO app (não
  // nativas como o AdMob): quem segura o slot com uma cobertura opaca precisa
  // tirá-la da frente enquanto elas estão abertas, senão elas abrem por baixo
  // e o fluxo fica esperando um toque que ninguém consegue dar.
  const telaPropria = (aberta) => {
    try { aoTelaPropria?.(aberta); } catch { /* quem escuta não derruba o slot */ }
  };
  if (st.ocupado) return nada;
  try {
    if (!st.uid || !admob.nativo()) return nada;
    st.ocupado = true;
    const est = await estadoAtual();
    if (!anunciosPossiveis(est) || !est.cfg.ADMOB_ATIVO || !est.cfg.INTERSTICIAL_ATIVO) {
      return nada;
    }
    if (!st.decisoes.has(gatilho)) anteciparDecisao(gatilho);
    const d = st.decisoes.get(gatilho);
    st.decisoes.delete(gatilho);
    const decisao = await comTimeout(d?.promessa, 3000, { mostrar: false });
    if (!decisao?.mostrar) {
      // O servidor já calcula POR QUE não mostra (CADENCIA, CARENCIA,
      // TETO_DIA, INTERVALO, GATILHO_DESLIGADO...). Jogar esse motivo fora
      // deixava "o anúncio não apareceu" indistinguível de um bug. Sem motivo
      // é porque o DECIDIR não voltou dentro do orçamento.
      const motivo = decisao?.motivo || 'SEM_RESPOSTA';
      registrarMetrica('INTERSTICIAL_NAO_MOSTRADO');
      // O `evento` é o único campo que a callable de métrica lê, então o
      // motivo específico vai para o console do aparelho — é o que dá para
      // inspecionar num build instalado, sem esperar painel.
      console.info('[ads] sem intersticial em %s: %s', gatilho, motivo);
      return nada;
    }

    let formato = null;
    if (est.cfg.CAMPANHA_PROPRIA_PRIORIDADE && st.campanhaPropria) {
      telaPropria(true);
      const ok = await comTimeout(st.campanhaPropria(), 90000, false);
      telaPropria(false);
      if (ok) formato = 'CAMPANHA';
    }
    if (!formato) {
      const ok = await admob.mostrarIntersticial(est.cfg);
      if (ok) formato = 'ADMOB';
    }
    if (!formato) {
      intersticial(gatilho, 'FALHOU').catch(() => {});
      return nada;
    }
    const r = await comTimeout(intersticial(gatilho, 'EXIBIDO', formato), 4000, null);
    const oferta = !!r?.oferta && (st.podeAdsCliente || est.modoTeste === true);
    if (oferta && st.host) {
      // Fica aberta (sem cobertura): o funil decide quando o fluxo segue.
      telaPropria(true);
      await comTimeout(st.host.abrirFunil({ origem: 'POS_INTERSTICIAL', inicio: 'oferta' }),
        30 * 60000, null);
    }
    return { exibido: true, oferta };
  } catch {
    return nada;
  } finally {
    st.ocupado = false;
  }
}

/**
 * O rewarded do AdMob (com SSV) cobre este tipo agora? Síncrono, pelo último
 * estado lido. Quando cobre, o vídeo próprio com timer de 30s (que o servidor
 * não consegue verificar) NÃO é mais oferecido.
 * @param {'BONUS'|'CONGELAR'|'SCANFOOD'} tipo
 */
export function rewardedAdmobLigado(tipo) {
  const est = st.ultimoEstado;
  const cfg = est?.cfg;
  if (!anunciosPossiveis(est) || !cfg?.ADMOB_ATIVO || !cfg?.ADMOB_REWARDED_ATIVO) return false;
  if (tipo === 'SCANFOOD') return !!cfg.SCANFOOD_VIDEO_ATIVO;
  if (tipo === 'CONGELAR') return !!cfg.CONGELAR_VIDEO_ATIVO;
  return !!cfg.BONUS_ATIVO;
}

/**
 * O vídeo-bônus pode ser oferecido para este crédito? (A palavra final é do
 * servidor, em prepararRewarded — aqui é só para não mostrar um botão que
 * certamente vai falhar.)
 * @param {object} moeda `moeda` devolvido pela ação ({creditou, valorFinal, eventKey})
 * @param {object} est estado de carregarEstadoAnuncios
 * @return {boolean}
 */
export function bonusDisponivel(moeda, est) {
  const cfg = est?.cfg || {};
  return !!(
    anunciosPossiveis(est) &&
    cfg.ADMOB_ATIVO && cfg.ADMOB_REWARDED_ATIVO && cfg.BONUS_ATIVO &&
    moeda?.creditou && Number(moeda.valorFinal) > 0 && moeda.eventKey
  );
}

/**
 * O "Multiplicar com vídeo" da atividade GPS pode aparecer? Mesmas chaves do
 * bônus de um crédito (ADMOB_ATIVO, ADMOB_REWARDED_ATIVO, BONUS_ATIVO e quem
 * vê anúncio, inclusive UIDS_TESTE). A palavra final é do servidor, em
 * prepararRewarded com `atividadeId`.
 * @param {{atividadeId:string, valor:number}} alvo a atividade e o que ela rendeu
 * @param {object} est estado de carregarEstadoAnuncios
 * @return {boolean}
 */
export function bonusAtividadeDisponivel(alvo, est) {
  const cfg = est?.cfg || {};
  return !!(
    anunciosPossiveis(est) &&
    cfg.ADMOB_ATIVO && cfg.ADMOB_REWARDED_ATIVO && cfg.BONUS_ATIVO &&
    alvo?.atividadeId && Number(alvo.valor) > 0
  );
}

// Quanto a TELA espera pelo SSV antes de liberar a pessoa. Curto de propósito:
// o callback do Google normalmente chega em 1–2s, e prender a tela por mais que
// isso para os casos lentos era o que fazia a verificação "demorar muito".
const ESPERA_SSV_MS = 5000;
// Depois de liberar a tela, a escuta CONTINUA neste orçamento maior: quando o
// SSV chega atrasado, o bônus ainda é creditado e a animação ainda roda.
const ESPERA_SSV_TARDIA_MS = 45000;

/** Espera o servidor mudar a sessão (o SSV do Google chegou). */
function esperarSessao(sessaoId, ms) {
  return new Promise((resolve) => {
    let unsub = null;
    const t = setTimeout(() => {
      unsub?.();
      resolve(null);
    }, ms);
    unsub = onSnapshot(
      doc(db, 'PACIENTE', st.uid, 'REWARDED_SESSOES', sessaoId),
      (snap) => {
        const d = snap.data();
        if (d && d.STATUS && d.STATUS !== 'PENDENTE') {
          clearTimeout(t);
          unsub?.();
          resolve(d);
        }
      },
      () => {
        clearTimeout(t);
        unsub?.();
        resolve(null);
      },
    );
  });
}

/**
 * Vídeo com recompensa, verificado pelo servidor.
 * @param {{tipo:'BONUS'|'CONGELAR'|'SCANFOOD', eventKeyOrigem?:string,
 *   atividadeId?:string}} args
 * @return {Promise<{status:'CONCLUIDO'|'NAO_FINALIZADO'|'AGUARDANDO'|'SEM_ANUNCIO'|
 *   'RECUSADO'|'ERRO',
 *   valorBonus?:number, saldoApos?:number, resultado?:object, motivo?:string}>}
 */
let _bonusPreparado = null; // { chave, promessa } da sessão já aberta

const chaveDoBonus = (tipo, eventKeyOrigem, atividadeId) =>
  `${tipo}|${eventKeyOrigem || ''}|${atividadeId || ''}`;

/**
 * Abre a sessão do rewarded e JÁ MANDA O VÍDEO CARREGAR, sem exibir nada.
 *
 * Para chamar quando a tela que oferece o vídeo monta. Sem isto, a sessão e a
 * carga do anúncio aconteciam as duas depois do toque, uma atrás da outra — e
 * era esse encadeamento que fazia o botão parecer travado.
 *
 * Idempotente por (tipo, eventKeyOrigem, atividadeId): duas chamadas não abrem
 * duas sessões.
 * @param {{tipo:string, eventKeyOrigem?:string, atividadeId?:string}} args
 *   `atividadeId` (só BONUS): bônus da atividade GPS inteira (treino + km).
 * @return {Promise<?{sessao:object, est:object}>}
 */
export function prepararBonus({ tipo, eventKeyOrigem = null, atividadeId = null }) {
  if (!st.uid || !admob.nativo()) return Promise.resolve(null);
  const chave = chaveDoBonus(tipo, eventKeyOrigem, atividadeId);
  if (_bonusPreparado?.chave === chave) return _bonusPreparado.promessa;
  const promessa = (async () => {
    const est = await estadoAtual();
    if (!anunciosPossiveis(est) || !est.cfg.ADMOB_ATIVO || !est.cfg.ADMOB_REWARDED_ATIVO) {
      return null;
    }
    const sessao = await prepararRewarded(
      tipo, eventKeyOrigem, Capacitor.getPlatform(), true, atividadeId,
    );
    if (sessao?.disponivel) {
      admob.preCarregarRewarded(est.cfg, {
        userId: sessao.userId, customData: sessao.customData,
      }).catch(() => {});
    }
    return { sessao, est };
  })().catch(() => null);
  _bonusPreparado = { chave, promessa };
  return promessa;
}

/** Traduz o doc da sessão no resultado que a tela consome. */
function resultadoDaSessao(d) {
  if (d.STATUS !== 'CONCLUIDO') return { status: 'RECUSADO', motivo: d.MOTIVO };
  const r = d.RESULTADO || {};
  return {
    status: 'CONCLUIDO',
    valorBonus: Number(r.valorBonus) || 0,
    saldoApos: r.saldoApos ?? null,
    resultado: r,
  };
}

export async function pedirRewarded({ tipo, eventKeyOrigem = null, atividadeId = null }) {
  try {
    if (!st.uid) return { status: 'ERRO' };
    const chave = chaveDoBonus(tipo, eventKeyOrigem, atividadeId);
    const pronto = await comTimeout(
      prepararBonus({ tipo, eventKeyOrigem, atividadeId }), 12000, null,
    );
    // A sessão é de uso único: sai do cache aqui, tenha dado certo ou não.
    if (_bonusPreparado?.chave === chave) _bonusPreparado = null;
    if (!pronto) return { status: 'SEM_ANUNCIO' };
    const { est, sessao } = pronto;
    if (!sessao?.disponivel) return { status: 'SEM_ANUNCIO', motivo: sessao?.motivo };

    const video = await admob.mostrarRewarded(est.cfg, {
      userId: sessao.userId,
      customData: sessao.customData,
    });
    if (video.semAnuncio) return { status: 'SEM_ANUNCIO' };
    // A sessão foi aberta na montagem da tela, então quem conta "o vídeo abriu"
    // é aqui — ver REWARDED_EXIBIDO na lista branca do servidor.
    if (video.exibido) registrarMetrica('REWARDED_EXIBIDO', { tipo });
    if (!video.recompensado) return { status: 'NAO_FINALIZADO' };

    // DAQUI PARA BAIXO O VÍDEO FOI ATÉ O FIM. O que ainda pode faltar é o SSV
    // do Google chegar ao servidor, e isso nunca é culpa de quem assistiu —
    // então não pode mais sair como 'NAO_FINALIZADO', que é o que mandava a
    // tela dizer "vídeo não finalizado" para quem tinha assistido tudo.
    //
    // Com IDs de TESTE o callback não chega NUNCA: os blocos de demonstração
    // do Google são da conta do Google, não dá para pendurar a URL de SSV
    // neles. Build de debug ⇒ o bônus é inalcançável por construção, e o
    // motivo abaixo é o que diz isso em vez de acusar a pessoa.
    const d = await esperarSessao(sessao.sessaoId, ESPERA_SSV_MS);
    if (d) return resultadoDaSessao(d);

    // Não chegou na janela curta. A tela é liberada AGORA com AGUARDANDO, e
    // `confirmacao` continua escutando: se o SSV chegar atrasado, quem está
    // esperando ainda credita e ainda roda a animação do total subindo.
    const teste = admob.usarIdsDeTeste();
    registrarMetrica('REWARDED_SSV_LENTO', { idsDeTeste: teste });
    return {
      status: 'AGUARDANDO',
      motivo: teste ? 'TESTE_SEM_SSV' : 'SSV_LENTO',
      confirmacao: esperarSessao(sessao.sessaoId, ESPERA_SSV_TARDIA_MS)
          .then((tardio) => (tardio
            ? resultadoDaSessao(tardio)
            : { status: 'ERRO', motivo: 'TIMEOUT' })),
    };
  } catch {
    return { status: 'ERRO' };
  }
}

/** Abre o funil do Premium (oferta → lembrete → comparativo → planos). */
export async function abrirFunilPremium(opts = {}) {
  if (!st.host) return;
  await st.host.abrirFunil({ origem: opts.origem || 'MANUAL', inicio: opts.inicio || 'oferta' });
}

/** Comprou o Premium: anúncios somem NA HORA, sem esperar o webhook. */
export function marcarPremiumLocal() {
  st.podeAdsCliente = false;
  st.decisoes.clear();
}
